import type { FC } from "react";
import React from "react";
import { ZButton } from "@/shared/components/button";
import { ZModal, ZModalContent, ZModalHeader, ZModalBody, ZModalFooter } from "@/shared/components/modal";

// Styles to ensure modal is visible
const modalStyles = {
  backdrop: {
    background: "rgba(0, 0, 0, 0.5)",
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1000
  },
  modal: {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1001,
    background: "white",
    borderRadius: "14px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
    width: "90%",
    maxWidth: "450px"
  }
};

interface WarningModalProps {
  /**
   * Handler called when the user confirms to proceed
   */
  onConfirm: () => void;
  
  /**
   * Handler called when the modal is closed without confirming
   */
  onClose: () => void;
  
  /**
   * Whether the modal is open or not
   */
  isOpen: boolean;
  
  /**
   * The title of the warning modal
   * @default "Proceed without Documents for Some Controls?"
   */
  title?: string;

  /**
   * The primary message to display
   * @default "Documents missing for some controls."
   */
  message?: string;

  /**
   * Additional message below the warning icon
   * @default "The ones without documents will not be assessed. You will have an option to assess them later."
   */
  additionalMessage?: string;
}

const WarningModal: FC<WarningModalProps> = ({
  onConfirm,
  onClose,
  isOpen,
  title = "Proceed without Documents for Some Controls?",
  message = "Documents missing for some controls.",
  additionalMessage = "The ones without documents will not be assessed. You will have an option to assess them later.",
}) => {
  
  return (
    <ZModal

      isOpen={isOpen}
      onOpenChange={onClose}
    >
      <ZModalContent className="max-w-xl ">
        <ZModalHeader className="flex text-lg flex-col w-full items-center pb-0">
          <p className="text-[20px] font-semibold">{title}</p>
        </ZModalHeader>
        <ZModalBody className="pt-4 pb-2">
          <div className="flex flex-col items-center">
            <p className="text-[14px] text-[#dd3961] text-center mb-6 font-medium">
              {message}
            </p>

            {/* Warning triangle with gradient background */}
            <div className="w-[200px] h-[200px] mb-6 flex items-center justify-center">
              <svg width="200" height="200" viewBox="0 0 240 240" fill="none" xmlns="http://www.w3.org/2000/svg">
                <title>Warning Triangle</title>
                {/* Gradient background blob */}
                <path d="M175.196 162.145C198.428 155.425 217.09 143.717 221.953 118.767C226.225 96.8598 221.977 69.5478 204.572 53.967C187.263 38.4678 159.634 37.647 139.671 48.207C118.926 59.2086 111.889 82.7046 90.1256 92.7414C80.0834 97.3556 69.0438 99.3757 58.0184 98.6166C48.2792 98.0166 38.0504 94.3974 28.7384 99.0006C6.23602 110.103 19.4216 142.172 31.844 155.818C51.188 177.073 69.5864 168.063 94.244 165.418C121.916 162.466 147.582 170.146 175.196 162.145Z" fill="url(#paint0_linear_731_21556)"/>
                <path opacity="0.9" d="M175.196 162.145C198.428 155.425 217.09 143.717 221.953 118.767C226.225 96.8598 221.977 69.5478 204.572 53.967C187.263 38.4678 159.634 37.647 139.671 48.207C118.926 59.2086 111.889 82.7046 90.1256 92.7414C80.0834 97.3556 69.0438 99.3757 58.0184 98.6166C48.2792 98.0166 38.0504 94.3974 28.7384 99.0006C6.23602 110.103 19.4216 142.172 31.844 155.818C51.188 177.073 69.5864 168.063 94.244 165.418C121.916 162.466 147.582 170.146 175.196 162.145Z" fill="white"/>
                
                {/* Shadow */}
                <path d="M119.997 205.229C171.396 205.229 213.064 202.796 213.064 199.795C213.064 196.794 171.396 194.361 119.997 194.361C68.5973 194.361 26.9297 196.794 26.9297 199.795C26.9297 202.796 68.5973 205.229 119.997 205.229Z" fill="#F5F5F5"/>
                
                {/* Warning triangle */}
                <g clipPath="url(#clip0_731_21556)">
                  <path d="M108.652 77.8818C113.917 68.7622 127.08 68.7622 132.345 77.8818L173.081 148.439C178.347 157.559 171.765 168.958 161.235 168.958H79.7623C69.2319 168.958 62.6504 157.559 67.9156 148.439L108.652 77.8818Z" fill="white" stroke="#71717A" strokeWidth="4"/>
                  <rect width="40" height="4" rx="2" transform="matrix(4.37114e-08 1 1 -4.37114e-08 118.5 96.2659)" fill="#71717A"/>
                  <rect width="6" height="6" rx="3" transform="matrix(4.37114e-08 1 1 -4.37114e-08 117.5 144.266)" fill="#71717A"/>
                </g>
                
                {/* Gradient definitions */}
                <defs>
                  <linearGradient id="paint0_linear_731_21556" x1="120.012" y1="41.1582" x2="120.012" y2="169.582" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#DD9AB0"/>
                    <stop offset="1" stopColor="#A982CF"/>
                  </linearGradient>
                  <clipPath id="clip0_731_21556">
                    <rect width="120" height="107.143" fill="white" transform="translate(60.5 66.4287)"/>
                  </clipPath>
                </defs>
              </svg>
            </div>

            <p className="text-[14px] text-[#777780] text-center mb-2">
              {additionalMessage}
            </p>
          </div>
        </ZModalBody>
        <ZModalFooter className="justify-center gap-4 pt-2 pb-6">
          <ZButton
            variant="bordered"
            size="sm"
            className="px-3 py-1 text-medium flex-1 border-1"
            onClick={onClose}
          >
            Discard
          </ZButton>
          <ZButton
            size="sm"
            className="px-3 py-1 text-medium flex-1 bg-primary text-white"
            onClick={onConfirm}
          >
            Yes, Proceed
          </ZButton>
        </ZModalFooter>
      </ZModalContent>
    </ZModal>
  );
};

export default WarningModal; 